import React from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, useTheme } from '@mui/material';
import { MdClose } from 'react-icons/md';
import Button from './Button';

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: React.ReactNode;
  icon?: React.ReactNode;
  cancelText?: string;
  confirmText?: string;
  confirmVariant?: 'primary' | 'danger';
  iconBackgroundColor?: string;
  isPending?: boolean;
  loadingText?: string;
}

export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  icon,
  cancelText = 'Cancel',
  confirmText = 'Confirm',
  confirmVariant = 'primary',
  iconBackgroundColor,
  isPending = false,
  loadingText
}: ConfirmModalProps) {
  const theme = useTheme();
  const isDanger = confirmVariant === 'danger';

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            p: 1.5,
            position: 'relative'
          }
        }
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={isPending}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: 'text.secondary'
        }}
      >
        <MdClose size={20} />
      </IconButton>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 3.5, pb: 1.5 }}>
        {/* Icon Circle */}
        {icon && (
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: iconBackgroundColor || (isDanger ? '#FEE2E2' : theme.palette.primary.light),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}
          >
            {icon}
          </Box>
        )}

        {/* Title */}
        <Typography variant='h6' sx={{ fontWeight: 600, mb: 1, color: 'text.primary', textAlign: 'center' }}>
          {title}
        </Typography>

        {/* Description */}
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 4,
            px: 1,
            lineHeight: 1.5,
            fontSize: 14
          }}
        >
          {description}
        </Typography>

        {/* Actions */}
        <Box display='flex' gap={1.5} justifyContent='flex-end' width='100%'>
          <Button
            variant='outlined'
            onClick={onClose}
            disabled={isPending}
            sx={{
              borderRadius: '24px',
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              px: 3.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                bgcolor: '#F9FAFB'
              }
            }}
          >
            {cancelText}
          </Button>
          <Button
            variant='contained'
            onClick={onConfirm}
            isPending={isPending}
            loadingText={loadingText}
            sx={{
              borderRadius: '24px',
              bgcolor: isDanger ? theme.palette.error.main : theme.palette.primary.main,
              color: '#FFFFFF',
              px: 3.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isDanger ? theme.palette.error.dark : theme.palette.primary.dark,
                boxShadow: 'none'
              }
            }}
          >
            {confirmText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
